"use client";

import React, { useEffect, useState } from "react";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import {
  Factory,
  TrendingUp,
  ShieldCheck,
  LayoutGrid,
  Phone,
  Send,
  AlertTriangle,
  Loader2,
  Package,
  CheckCircle2,
  ExternalLink,
} from "lucide-react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useRouter } from "next/navigation";
import { findAllActiveCategory } from "@/modules/category/category.service";
import {
  insertInquiry,
  inquiryImagesUpload,
} from "@/modules/cms/inquiry.service";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { InputField } from "@/components/common/FormFields/InputField";
import { SelectField } from "@/components/common/FormFields/SelectField";
import { TextAreaField } from "@/components/common/FormFields/TextAreaField";
import { MultiImageUploadField } from "@/components/common/FormFields/MultiImageUploadField";
import {
  ESTIMATED_BUDGET_OPTIONS,
  ESTIMATED_ORDER_PIECE_OPTIONS,
  INQUIRY_TYPES,
  PLATFORM_OPTIONS,
} from "@/helper/common";
import { ISetting } from "@/modules/setting/settingModel";
import Link from "next/link";
import { FaWhatsapp } from "react-icons/fa";
import { ICMS } from "../cms/cmsModel";
import { createLookupObject } from "@/helper/helper";

const WholesaleSchema = Yup.object().shape({
  FirstName: Yup.string().required("First name is required"),
  LastName: Yup.string().required("Last name is required"),
  Email: Yup.string().email("Invalid email").required("Email is required"),
  MobileNo: Yup.string()
    .matches(/^[0-9]{10}$/, "Mobile number must be 10 digits")
    .required("Mobile number is required"),
  Address: Yup.string().required("Address is required"),
  Platform: Yup.string().required("Platform is required"),
  OrderBudget: Yup.string().optional(),
  Qty: Yup.number()
    .required("Order quantity is required")
    .min(1, "Order quantity must be greater than 0"),
  InquiryType: Yup.string().required("Inquiry type is required"),
  CategoryIDs: Yup.array()
    .min(1, "Select at least one category")
    .required("Category is required"),
  Remarks: Yup.string().optional(),
  Images: Yup.array().optional(),
});

const INITIAL_VALUES = {
  FirstName: "",
  LastName: "",
  Email: "",
  MobileNo: "",
  Address: "",
  Platform: "",
  OrderBudget: "",
  Qty: "",
  InquiryType: "Wholesale",
  CategoryIDs: [],
  Remarks: "",
  Images: [],
};
interface WholesaleProps {
  cmsData: ICMS[];
}
export default function WholesaleClient({ cmsData }: WholesaleProps) {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [categories, setCategories] = useState<any[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [CMSData, setCMSData] = React.useState<any>({});

  useEffect(() => {
    if (cmsData && cmsData?.length > 0) {
      setCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  const SettingList = useAppSelector((state) => state.home.SettingList);
  const Settings: Record<string, ISetting> = SettingList?.reduce(
    (acc, item) => {
      acc[item?.SettingKey] = item;
      return acc;
    },
    {} as Record<string, ISetting>,
  );

  const PhoneNo2 = Settings?.["PhoneNo2"]?.SettingValue;
  const WhatsAppNo = Settings?.["WhatsAppNo"]?.SettingValue;
  const WhatsAppNo2 = Settings?.["WhatsAppNo2"]?.SettingValue;
  const IndiaMartLink = Settings?.["IndiaMartLink"]?.SettingValue;
  const CallTiming = Settings?.["CallTiming"]?.SettingValue;
  const phone = WhatsAppNo ? WhatsAppNo.replace(/\D+/g, "") : "";
  const whatsappLink = phone
    ? `https://wa.me/${phone}?text=${encodeURIComponent("Hello, I'd like to know more details.")}`
    : "#";
  const phone2 = WhatsAppNo2 ? WhatsAppNo2.replace(/\D+/g, "") : "";
  const whatsappLink2 = phone2
    ? `https://wa.me/${phone2}?text=${encodeURIComponent("Hello, I'd like to know more details.")}`
    : "#";

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const result = await dispatch(findAllActiveCategory()).unwrap();
        setCategories(result || []);
      } catch (err) {
        console.error("Failed to load categories", err);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCats();
  }, [dispatch]);

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      const payload = {
        FirstName: values.FirstName,
        LastName: values.LastName,
        Email: values.Email,
        MobileNo: values.MobileNo,
        Address: values.Address,
        Platform: values.Platform,
        OrderBudget: values.OrderBudget,
        Qty: values.Qty,
        InquiryType: values.InquiryType,
        CategoryIDs: values.CategoryIDs.join(","),
        Remarks: values.Remarks,
      };

      const response = await insertInquiry(payload);
      // insertInquiry returns the ID of the new inquiry
      const inquiryID = response?.Data?.ID;

      if (inquiryID) {
        // If user selected any images, upload them
        if (values.Images && values.Images.length > 0) {
          await inquiryImagesUpload(inquiryID, values.Images);
        }
        router.push("/wholesale/thank-you");
      }
    } catch (error) {
      console.error("Submission Error:", error);
      // Errors are handled inside the service via SuccessToast/ErrorToast
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-black">
      <Navbar />

      {/* PAGE HERO */}
      {CMSData?.["Wholesale - Section 1 : Banner"] ? (
        <section
          className="banner-section border-b border-border-1 pt-24 pb-16 px-4 md:px-14 relative overflow-hidden"
          dangerouslySetInnerHTML={{
            __html: CMSData?.["Wholesale - Section 1 : Banner"]?.Template,
          }}
        />
      ) : (
        <section className="bg-black border-b border-border-1 pt-18 pb-14 px-4 md:px-14 relative overflow-hidden">
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-30 bg-[radial-gradient(ellipse,rgba(92,32,32,0.05)_0%,transparent_70%)] pointer-events-none" />
          <div className="flex items-center gap-3.5 text-[10px] font-semibold tracking-[0.28em] uppercase text-gold-core mb-4 before:h-px before:w-15 before:bg-gradient-to-r before:from-gold-dim before:to-transparent">
            Bulk Wholesale Only
          </div>
          <h1 className="font-cormorant text-[clamp(40px,6vw,72px)] font-light text-text-1 leading-[1.1] mb-3">
            Wholesale <em className="italic gold-text-gradient">Inquiry</em>
          </h1>
          <p className="text-base text-text-2 font-light leading-relaxed max-w-2xl">
            We are a <strong className="text-text-1">wholesale-only</strong>{" "}
            manufacturer.{" "}
            <strong className="text-text-1">
              Minimum 100 pieces per single design.
            </strong>{" "}
            Retailers, resellers, and E-commerce sellers (Flipkart, Amazon,
            Meesho, Myntra) — fill in the form for direct factory access.
          </p>
        </section>
      )}

      {/* BULK WARNING BANNER */}
      {CMSData?.["Wholesale - Section 2 : Important Notice"] && (
        <div
          className="bg-gradient-to-r from-black-2 via-red-50 to-black-2 border-y border-red-200/50 py-6 px-4 md:px-14 flex flex-col md:flex-row items-center gap-6"
          dangerouslySetInnerHTML={{
            __html:
              CMSData?.["Wholesale - Section 2 : Important Notice"]?.Template,
          }}
        />
      )}
      {/* <div className="bg-linear-to-r from-black via-[#180800] to-black border-y border-red-900/30 py-6 px-4 md:px-14 flex flex-col md:flex-row items-center gap-6">
        <AlertTriangle className="text-red-500 shrink-0" size={32} />
        <div className="flex-1 text-center md:text-left">
          <h3 className="text-sm font-bold text-red-400 tracking-wide mb-1 uppercase">
            Important — Bulk Wholesale Only. Minimum 100 Pcs Per Design.
          </h3>
          <p className="text-xs text-text-2 font-light leading-relaxed">
            NakMan Jewellery is a wholesale manufacturer. We only accept volume
            enquiries. We do not supply small retail quantities or mixed small
            lots.
          </p>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-6 py-3 text-center rounded-sm shrink-0">
          <div className="text-[10px] font-bold uppercase tracking-widest">
            Bulk Only
          </div>
          <div className="text-xl font-bold">100+ Pcs</div>
          <div className="text-[9px] font-medium opacity-70">per design</div>
        </div>
      </div> */}

      {/* CATALOGUE NOTICE */}
      {CMSData?.["Wholesale - Section 3 : Need More"] && (
        <section
          className="bg-gradient-to-r from-black-2 via-black to-black-2 border-b border-border-1 py-10 px-4 md:px-14 flex flex-col lg:flex-row items-center gap-10"
          dangerouslySetInnerHTML={{
            __html: CMSData?.["Wholesale - Section 3 : Need More"]?.Template,
          }}
        />
      )}
      {/* <section className="bg-linear-to-r from-[#1A1200] via-[#221800] to-[#1A1200] border-b border-gold-dim/30 py-10 px-4 md:px-14 flex flex-col lg:flex-row items-center gap-10">
        <div className="w-14 h-14 bg-gold-pale/20 border border-gold-dim/40 rounded-full flex items-center justify-center text-gold-core shrink-0">
          <LayoutGrid size={28} />
        </div>
        <div className="flex-1 text-center lg:text-left">
          <h3 className="font-cormorant text-2xl font-bold gold-text-gradient mb-2">
            Need More Than What's in Our Catalogue?
          </h3>
          <p className="text-sm text-text-2 leading-relaxed font-light">
            If you require products beyond our listed items, send us your
            Inquiry.{" "}
            <strong className="text-gold-core">
              Phone number is mandatory
            </strong>{" "}
            for WhatsApp contact.
          </p>
        </div>
        <div className="flex items-center gap-4 bg-gold-pale/30 border border-gold-dim/50 px-6 py-4 rounded-sm">
          <Phone className="text-gold-core" size={20} />
          <div className="text-left">
            <div className="text-[10px] font-bold uppercase tracking-widest text-gold-core">
              Mandatory
            </div>
            <div className="text-sm font-semibold text-text-1">
              <Link href={`tel:${PhoneNo2}`}>{PhoneNo2}</Link>
            </div>
          </div>
        </div>
      </section> */}

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-12 py-16 px-4 md:px-14 bg-black">
        {/* FORM AREA */}
        <div className="bg-black border border-border-1 p-8 md:p-12 relative overflow-hidden rounded-md shadow-2xl">
          {/* Maroon Accent Line */}
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-gold-core to-transparent opacity-60" />

          <div className="mb-14 relative">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-black-2 border border-gold-core/20 rounded-sm mb-6">
              <CheckCircle2 size={12} className="text-gold-core" />
              <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gold-core">
                Wholesale Application
              </span>
            </div>

            <h2 className="font-cormorant text-4xl md:text-5xl font-light text-text-1 mb-4 leading-tight">
              Manufacturer{" "}
              <em className="italic gold-text-gradient font-medium underline underline-offset-8 decoration-gold-dim/20">
                Direct Access
              </em>
            </h2>
            <p className="text-text-2 font-light max-w-lg leading-relaxed border-l-2 border-gold-dim/50 pl-5 py-1">
              For bulk enquiries and factory-direct pricing. Submit your
              business profile to verify your wholesale status.
            </p>
          </div>

          <Formik
            initialValues={INITIAL_VALUES}
            validationSchema={WholesaleSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              touched,
              errors,
              isSubmitting,
              setFieldValue,
              handleBlur,
              handleChange,
            }) => (
              <Form className="space-y-12">
                {/* Section 1: Basic Info */}
                <div className="space-y-8">
                  <div className="flex items-center gap-4">
                    <span className="w-8 h-px bg-gold-dim/30"></span>
                    <span className="text-xs font-bold uppercase tracking-[0.3em] text-text-3">
                      Personal Details
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <InputField
                      label="First Name"
                      name="FirstName"
                      placeholder="Enter First Name"
                      inputClassName="bg-white border-border-1 text-text-1 focus:border-gold-dim placeholder:text-text-3 text-sm"
                      required
                      variant="light"
                    />
                    <InputField
                      label="Last Name"
                      name="LastName"
                      placeholder="Enter Last Name"
                      inputClassName="bg-white border-border-1 text-text-1 focus:border-gold-dim placeholder:text-text-3 text-sm"
                      required
                      variant="light"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <InputField
                      label="Email Address"
                      name="Email"
                      type="email"
                      placeholder="i.e example@gmail.com"
                      inputClassName="bg-white border-border-1 text-text-1 focus:border-gold-dim placeholder:text-text-3 text-sm"
                      required
                      variant="light"
                    />
                    <InputField
                      label="Mobile Number"
                      name="MobileNo"
                      placeholder="i.e 9999999999"
                      inputClassName="bg-white border-border-1 text-text-1 focus:border-gold-dim placeholder:text-text-3 text-sm"
                      required
                      variant="light"
                    />
                  </div>
                </div>

                {/* Section 2: Business Info */}
                <div className="space-y-8">
                  <div className="flex items-center gap-4">
                    <span className="w-8 h-px bg-gold-dim/30"></span>
                    <span className="text-xs font-bold uppercase tracking-[0.3em] text-text-3">
                      Business Information
                    </span>
                  </div>

                  <TextAreaField
                    label="Full Business Address"
                    name="Address"
                    placeholder="Shop/Office name, Area, City, State, ZIP"
                    required
                    variant="light"
                    className="min-h-[100px]"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <SelectField
                      label="Primary Seller Platform"
                      name="Platform"
                      required
                      placeholder="Select Platform"
                      variant="light"
                      options={PLATFORM_OPTIONS}
                    />
                    <SelectField
                      label="Inquiry Type"
                      name="InquiryType"
                      required
                      variant="light"
                      options={INQUIRY_TYPES}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <InputField
                      label="Estimated Order Quantity"
                      name="Qty"
                      type="number"
                      placeholder="i.e. 100"
                      required
                      variant="light"
                      inputClassName="bg-white border-border-1 text-text-1 focus:border-gold-dim placeholder:text-text-3 text-sm"
                      min={0}
                    />
                    <SelectField
                      label="Estimated Order Budget"
                      name="OrderBudget"
                      placeholder="Select Range"
                      variant="light"
                      options={ESTIMATED_BUDGET_OPTIONS}
                    />
                  </div>

                  <div className="space-y-4">
                    <label className="text-[10px] text-text-3 font-bold tracking-widest uppercase flex items-center gap-2">
                      Interested Categories{" "}
                      <span className="text-red-400">*</span>
                    </label>
                    {loadingCategories ? (
                      <div className="flex items-center gap-3 py-6 text-text-3 animate-pulse">
                        <Loader2 className="animate-spin" size={16} />
                        <span className="text-xs tracking-widest uppercase font-bold">
                          Synchronizing...
                        </span>
                      </div>
                    ) : (
                      <div
                        className={`grid grid-cols-1 md:grid-cols-2 gap-4 p-5 bg-black-2 border ${touched.CategoryIDs && errors.CategoryIDs ? "border-red-500/50" : "border-border-1"} rounded-sm`}
                      >
                        {categories.map((cat) => (
                          <label
                            key={cat.ID}
                            className="flex items-center gap-3 text-[11px] text-text-2 hover:text-text-1 cursor-pointer transition-colors group"
                          >
                            <input
                              type="checkbox"
                              name="CategoryIDs"
                              value={cat.ID}
                              checked={values.CategoryIDs.includes(
                                cat.ID.toString() as never,
                              )}
                              onChange={(e) => {
                                const val = cat.ID.toString();
                                const nextValue = e.target.checked
                                  ? [...values.CategoryIDs, val]
                                  : values.CategoryIDs.filter(
                                      (v: any) => v !== val,
                                    );
                                setFieldValue("CategoryIDs", nextValue);
                              }}
                              className="accent-gold-core w-4 h-4 cursor-pointer"
                            />
                            <span className="group-hover:translate-x-1 transition-transform">
                              {cat.Title}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                    {touched.CategoryIDs && errors.CategoryIDs && (
                      <p className="text-[9px] text-red-400 uppercase tracking-widest font-semibold">
                        {errors.CategoryIDs as string}
                      </p>
                    )}
                  </div>

                  <TextAreaField
                    label="Special Remarks / Message"
                    name="Remarks"
                    placeholder="Share more details about your requirements..."
                    variant="dark"
                    className="min-h-[140px]"
                    inputClassName="bg-white border-border-1 text-text-1 focus:border-gold-dim placeholder:text-text-3 text-sm"
                  />

                  <div className="pt-4">
                    <MultiImageUploadField
                      label="Upload References / Designs (Optional)"
                      name="Images"
                      variant="dark"
                    />
                  </div>
                </div>

                <div className="pt-6">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="group relative w-full bg-linear-to-r from-gold-dim via-gold-core to-gold-dim p-px rounded-sm overflow-hidden transition-all duration-300 hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] active:scale-[0.98] cursor-pointer btn-gold"
                  >
                    <div className="bg-black h-full w-full py-5 flex items-center justify-center gap-3 bg-transparent transition-colors shadow-sm">
                      <span className="text-sm font-bold tracking-[0.25em] uppercase text-white group-hover:text-black transition-colors">
                        {isSubmitting ? "Sending Inquiry..." : "Submit Inquiry"}
                      </span>
                      {isSubmitting ? (
                        <Loader2
                          className="animate-spin text-white group-hover:text-black"
                          size={18}
                        />
                      ) : (
                        <Send
                          size={18}
                          className="text-white group-hover:text-black transition-colors"
                        />
                      )}
                    </div>
                  </button>
                  <p className="text-center text-[10px] text-text-3 uppercase tracking-widest mt-6">
                    Our team will contact you within 24 business hours
                  </p>
                </div>
              </Form>
            )}
          </Formik>
        </div>

        {/* SIDEBAR */}
        <div className="space-y-10">
          {CMSData?.["Wholesale - Section 4 : Why Partner With Us"] && (
            <div
              className="space-y-6"
              dangerouslySetInnerHTML={{
                __html:
                  CMSData?.["Wholesale - Section 4 : Why Partner With Us"]
                    ?.Template,
              }}
            />
          )}
          {/* <div className="space-y-6">
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-text-3 pb-2 border-b border-border-1">
              Why Partner With Us
            </div>

            <div className="space-y-5">
              {[
                {
                  icon: <Factory size={18} />,
                  title: "Direct Factory",
                  desc: "Manufactured in-house in Ahmedabad. No middlemen margins.",
                },
                {
                  icon: <LayoutGrid size={18} />,
                  title: "Custom Orders",
                  desc: "Need something unique? We arrange production for your designs.",
                },
                {
                  icon: <TrendingUp size={18} />,
                  title: "Stacked Discounts",
                  desc: "Loyalty + bulk slab discounts. Save up to 15% on your first order.",
                },
                {
                  icon: <ShieldCheck size={18} />,
                  title: "PAN India Delivery",
                  desc: "Trusted shipping via DTDC, Tirupati, Speed Post & more.",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="group p-5 bg-black-2 border border-border-1 border-l-2 border-l-gold-dim hover:border-l-gold-core transition-all"
                >
                  <div className="text-gold-core mb-2">{item.icon}</div>
                  <h4 className="text-[13px] font-bold text-text-1 mb-1">
                    {item.title}
                  </h4>
                  <p className="text-[11px] text-text-3 leading-relaxed font-light">
                    {item.desc}
                  </p>
                </div>
              ))}
            </div>
          </div> */}

          {(WhatsAppNo || WhatsAppNo2) && (
            <div className="bg-black-2 border border-border-1 p-6 space-y-6">
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-gold-core">
                Or Contact Directly
              </div>
              <div className="space-y-3">
                {WhatsAppNo && (
                  <Link
                    href={whatsappLink}
                    target="_blank"
                    className="flex items-center gap-4 p-4 bg-black border border-border-1 hover:border-gold-dim/40 transition-colors rounded-sm group shadow-sm"
                  >
                    <FaWhatsapp className="text-2xl text-[#25D366] group-hover:scale-110 transition-transform" />
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-text-3">
                        WhatsApp
                      </div>
                      <div className="text-sm font-bold text-text-1 tracking-wide">
                        {WhatsAppNo}
                      </div>
                    </div>
                  </Link>
                )}
                {WhatsAppNo2 && (
                  <Link
                    href={whatsappLink2}
                    target="_blank"
                    className="flex items-center gap-4 p-4 bg-black border border-border-1 hover:border-gold-dim/40 transition-colors rounded-sm group shadow-sm"
                  >
                    <FaWhatsapp className="text-2xl text-[#25D366] group-hover:scale-110 transition-transform" />
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-text-3">
                        WhatsApp
                      </div>
                      <div className="text-md font-bold text-text-1 tracking-wide">
                        {WhatsAppNo2}
                      </div>
                    </div>
                  </Link>
                )}
              </div>
              <div className="text-sm text-text-3 text-center italic">
                {CallTiming}
              </div>
            </div>
          )}

          {IndiaMartLink && (
            <a
              href={IndiaMartLink}
              target="_blank"
              className="flex items-center justify-center gap-2 w-full py-4 border border-border-1  font-bold uppercase tracking-widest bg-linear-to-r from-gold-dim via-gold-core to-gold-dim p-px rounded-sm overflow-hidden transition-all duration-300 hover:shadow-[0_0_30px_rgba(212,175,55,0.3)] active:scale-[0.98] cursor-pointer group text-white btn-gold"
            >
              <CheckCircle2 size={14} className="text-gold-core" />
              Verify Us on IndiaMart
              <ExternalLink
                size={12}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </a>
          )}
        </div>
      </div>

      <Footer />
    </main>
  );
}
